/**
 * Modern cookie management utility supporting SameSite, Secure, Max-Age, and modern JS standards.
 */
const docCookies = {
    /**
     * Get cookie value by key name.
     * @param {string} key
     * @returns {string|null}
     */
    getItem(key) {
        if (!key) return null;
        const encodedKey = encodeURIComponent(key).replace(/[\-\.\+\*]/g, "\\$&");
        const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${encodedKey}\\s*=\\s*([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
    },

    /**
     * Set a cookie key-value pair with flexible expiry options.
     * @param {string} key
     * @param {string} value
     * @param {number|string|Date} [end] - Max-age in seconds, UTC date string, or Date object
     * @param {string} [path="/"]
     * @param {string} [domain]
     * @param {boolean} [secure=false]
     * @param {"Strict"|"Lax"|"None"} [sameSite="Lax"]
     * @returns {boolean}
     */
    setItem(key, value, end, path = "/", domain, secure = false, sameSite = "Lax") {
        if (!key || /^(?:expires|max\-age|path|domain|secure|samesite)$/i.test(key)) {
            return false;
        }

        let expires = "";

        if (end) {
            if (typeof end === "number") {
                expires = end === Infinity ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT" : `; max-age=${end}`;
            } else if (typeof end === "string") {
                expires = `; expires=${end}`;
            } else if (end instanceof Date) {
                expires = `; expires=${end.toUTCString()}`;
            }
        }

        const cookieString = [
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
            expires,
            domain ? `; domain=${domain}` : "",
            path ? `; path=${path}` : "",
            secure ? "; secure" : "",
            sameSite ? `; samesite=${sameSite}` : ""
        ].join("");

        document.cookie = cookieString;
        return true;
    },

    /**
     * Remove a cookie by setting its expiration date in the past.
     * @param {string} key
     * @param {string} [path="/"]
     * @param {string} [domain]
     * @returns {boolean}
     */
    removeItem(key, path = "/", domain) {
        if (!key || !this.hasItem(key)) return false;

        document.cookie = [
            `${encodeURIComponent(key)}=`,
            "; expires=Thu, 01 Jan 1970 00:00:00 GMT",
            domain ? `; domain=${domain}` : "",
            path ? `; path=${path}` : ""
        ].join("");

        return true;
    },

    /**
     * Check if a cookie exists.
     * @param {string} key
     * @returns {boolean}
     */
    hasItem(key) {
        if (!key) return false;
        const encodedKey = encodeURIComponent(key).replace(/[\-\.\+\*]/g, "\\$&");
        return new RegExp(`(?:^|;\\s*)${encodedKey}\\s*=`).test(document.cookie);
    },

    /**
     * Retrieve all cookie keys as an array.
     * @returns {string[]}
     */
    keys() {
        if (!document.cookie) return [];
        return document.cookie
            .split(/;\s*/)
            .map(cookie => decodeURIComponent(cookie.split("=")[0]))
            .filter(Boolean);
    }
};

export default docCookies;